<template>
  <v-container class="check-email-page py-8" fluid>
    <v-layout justify-center>
      <v-flex xs12 sm10 md8 lg7>
        <v-card class="email-card" elevation="8">
          <div class="hero-panel">
            <div class="hero-kicker">Business Suite Security</div>
            <h1 class="hero-title">Unlock Business Features</h1>
            <p class="hero-subtitle">
              Verify your email to continue securely to your business setup.
            </p>
          </div>

          <v-card-text class="pa-6 pa-sm-7">
            <v-alert v-if="errorMessage" :value="true" type="error" outline class="mb-4">
              {{ errorMessage }}
            </v-alert>

            <div class="status-row">
              <div>
                <p class="status-label mb-1">Verification status</p>
                <v-chip :color="isVerified ? 'teal darken-1' : 'orange darken-2'" text-color="white" small>
                  {{ isVerified ? "Email verified" : "Email verification required" }}
                </v-chip>
              </div>

              <v-btn color="primary" depressed @click="openEmailDialog">
                {{ isVerified ? "Update email" : "Add my email" }}
              </v-btn>
            </div>

            <v-btn
              class="continue-btn mt-6"
              color="teal darken-2"
              large
              block
              :disabled="!isVerified || loading"
              :loading="loading"
              @click="verifyAndContinue"
            >
              Continue
            </v-btn>

            <p class="helper-text mt-3 mb-0">
              Once the code is valid, you are redirected automatically to the page you originally requested.
            </p>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="dialog" max-width="540" persistent>
      <v-card class="dialog-card">
        <v-card-title class="pb-2">
          <div>
            <p class="dialog-step mb-1">{{ needEmail ? "Step 2 of 2" : "Step 1 of 2" }}</p>
            <h2 class="dialog-title mb-1">{{ needEmail ? "Enter verification code" : "Add your email address" }}</h2>
            <p class="dialog-subtitle mb-0">
              {{
                needEmail
                  ? "A 6-digit code was sent to your email. Enter it to continue."
                  : "We use your email only for account security and recovery."
              }}
            </p>
          </div>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-text-field
            v-if="!needEmail"
            v-model="email"
            label="Email address"
            type="email"
            outline
            prepend-icon="mail"
            @keyup.enter.native="addEmail"
          />

          <v-text-field
            v-else
            v-model="verificationCode"
            label="Verification code"
            type="text"
            outline
            maxlength="6"
            counter
            prepend-icon="verified_user"
            @keyup.enter.native="submitVerificationCode"
          />
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-btn text color="grey darken-1" :disabled="isLoadingAdd || isLoading" @click="closeDialog">
            Cancel
          </v-btn>
          <v-spacer />

          <v-btn
            v-if="!needEmail"
            color="primary"
            depressed
            :loading="isLoadingAdd"
            :disabled="isLoadingAdd"
            @click="addEmail"
          >
            Send code
          </v-btn>

          <v-btn
            v-else
            color="teal darken-2"
            depressed
            :loading="isLoading"
            :disabled="isLoading"
            @click="submitVerificationCode"
          >
            Verify and continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      verificationCode: '',
      email: '',
      needEmail: false,
      dialog: false,
      isLoading: false,
      isLoadingAdd: false,
      userProfile: null,
      isVerified: false,
      errorMessage: null,
      loading: false,
    };
  },
  methods: {
    validateEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    getRedirectTarget() {
      const redirect = this.$route.query.redirect;
      if (typeof redirect === 'string' && redirect.startsWith('/')) {
        return redirect;
      }
      return '/create_business_account4';
    },
    showSnack(message, color = 'teal darken-4') {
      this.$store.commit('setSnackBarMessage', message);
      this.$store.commit('setSnackBarColor', color);
      this.$store.commit('activateSnackBar', true);
    },
    openEmailDialog() {
      this.dialog = true;
      this.needEmail = false;
      this.verificationCode = '';
    },
    closeDialog() {
      this.dialog = false;
      this.needEmail = false;
      this.verificationCode = '';
    },
    async addEmail() {
      if (!this.validateEmail(this.email)) {
        this.showSnack('Please enter a valid email address.', 'error');
        return;
      }

      this.isLoadingAdd = true;
      try {
        await axios.post('/api/v1/savings/add-email/', { email: this.email });
        this.$store.dispatch('updateEmail', this.email);
        this.needEmail = true;
        this.showSnack('A verification code has been sent to your email.');
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Error while adding the email.';
        this.showSnack(errorMessage, 'error');
      } finally {
        this.isLoadingAdd = false;
      }
    },
    async submitVerificationCode() {
      if (!this.verificationCode || this.verificationCode.trim().length !== 6) {
        this.showSnack('Please enter the 6-digit verification code.', 'error');
        return;
      }

      this.isLoading = true;
      try {
        const response = await axios.post('/api/v1/savings/code/', {
          verification_code: this.verificationCode.trim(),
        });

        this.showSnack(response.data.success || 'Verification successful!');
        this.isVerified = true;
        this.$store.dispatch('updateVerificationStatus', true);

        this.closeDialog();
        this.verificationCode = '';
        this.verifyAndContinue();
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Error while verifying the code.';
        this.showSnack(errorMessage, 'error');
      } finally {
        this.isLoading = false;
      }
    },
    async fetchUserProfile() {
      this.loading = true;
      this.errorMessage = null;

      try {
        const response = await axios.get('/api/v1/savings/user-profile/');
        const profile = response.data || {};
        this.userProfile = profile;

        this.isVerified = Boolean(
          profile.isVerified || profile.is_verified || profile.email_verified
        );

        if (profile.email) {
          this.email = profile.email;
        }

        this.$store.dispatch('updateVerificationStatus', this.isVerified);
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error ||
          'Unable to load your profile right now. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    verifyAndContinue() {
      if (!this.isVerified) {
        this.showSnack('Your email is not verified yet.', 'error');
        return;
      }

      this.$router.replace(this.getRedirectTarget());
    },
  },
  mounted() {
    this.fetchUserProfile();
  },
};
</script>

<style scoped>
.check-email-page {
  min-height: calc(100vh - 64px);
  background:
    radial-gradient(circle at 10% 15%, rgba(6, 182, 212, 0.18), transparent 45%),
    radial-gradient(circle at 90% 0%, rgba(13, 148, 136, 0.16), transparent 35%),
    linear-gradient(180deg, #f6fbfb 0%, #ffffff 100%);
}

.email-card {
  border-radius: 20px;
  overflow: hidden;
}

.hero-panel {
  padding: 32px 28px;
  background: linear-gradient(120deg, #0f766e 0%, #0891b2 100%);
  color: #fff;
}

.hero-kicker {
  letter-spacing: 0.08em;
  font-size: 12px;
  text-transform: uppercase;
  opacity: 0.9;
}

.hero-title {
  margin: 8px 0 6px;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 700;
}

.hero-subtitle {
  margin: 0;
  font-size: 15px;
  opacity: 0.94;
}

.status-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.status-label {
  color: #0f172a;
  font-weight: 600;
  font-size: 14px;
}

.continue-btn {
  border-radius: 12px;
  text-transform: none;
  font-weight: 700;
}

.helper-text {
  color: #475569;
  font-size: 13px;
}

.dialog-card {
  border-radius: 16px;
}

.dialog-step {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}

.dialog-title {
  font-size: 22px;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 14px;
  color: #475569;
}

@media (max-width: 600px) {
  .hero-panel {
    padding: 22px 18px;
  }

  .hero-title {
    font-size: 24px;
  }

  .status-row {
    align-items: stretch;
  }
}
</style>
